import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { useFetchGarageInfo } from 'domains/garage/Garage.requests';
import { updateSearchRateLimitConfigRequest } from 'domains/searchRateLimit/SearchRateLimit.store';
import { GarageLimitOverride, SearchRateLimitConfig } from 'domains/searchRateLimit/SearchRateLimit.types';
import { BlackButton, Flex, Input, InputNumber, MarginBox, Modal, Text, WhiteButton, YellowButton } from 'UI';
import { getData } from 'utils';

interface OverrideActionsCellProps {
  garageId: string;
  isConfirming: boolean;
  onRequestRemove: (id: string) => void;
  onConfirmRemove: (id: string) => void;
  onCancelRemove: () => void;
}

const OverrideActionsCell = ({
  garageId,
  isConfirming,
  onRequestRemove,
  onConfirmRemove,
  onCancelRemove,
}: OverrideActionsCellProps) => {
  const { t } = useTranslation();
  const handleRequestRemove = useCallback(() => onRequestRemove(garageId), [garageId, onRequestRemove]);
  const handleConfirmRemove = useCallback(() => onConfirmRemove(garageId), [garageId, onConfirmRemove]);

  if (isConfirming) {
    return (
      <Flex gap={8} align={'center'}>
        <Text type="base">{t('common.confirm', 'Confirm?')}</Text>
        <BlackButton onClick={handleConfirmRemove} data-cy={'confirm-remove-override-' + garageId}>
          {t('common.yes', 'Yes')}
        </BlackButton>
        <WhiteButton onClick={onCancelRemove}>
          <Text type={'h4_paragraph'}>{t('common.no', 'No')}</Text>
        </WhiteButton>
      </Flex>
    );
  }

  return (
    <WhiteButton onClick={handleRequestRemove} data-cy={'remove-override-' + garageId}>
      <Text type={'h4_paragraph'}>{t('common.remove', 'Remove')}</Text>
    </WhiteButton>
  );
};

interface GarageOverridesListProps {
  countryCode: string;
  config: SearchRateLimitConfig | null;
  onSuccess?: () => void;
}

export const GarageOverridesList: React.FC<GarageOverridesListProps> = ({
  countryCode: _countryCode,
  config,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [newOverride, setNewOverride] = useState<Partial<GarageLimitOverride>>({
    garageId: '',
    ipn: '',
    shortTermLimit: undefined,
    longTermLimit: undefined,
  });

  const garageInfo = useFetchGarageInfo(newOverride.garageId ?? '', 'TRESOR');
  const resolvedGarageName = getData(garageInfo)?.name;

  const overrides = useMemo(() => config?.garageOverrides ?? {}, [config]);
  const overridesList = useMemo(() => Object.values(overrides), [overrides]);

  const openModal = useCallback(() => setShowModal(true), []);

  const resetModal = useCallback(() => {
    setNewOverride({ garageId: '', ipn: '', shortTermLimit: undefined, longTermLimit: undefined });
    setShowModal(false);
  }, []);

  const handleAddOverride = useCallback(() => {
    if (!newOverride.garageId || !newOverride.ipn || !config) {
      return;
    }
    const override: GarageLimitOverride = {
      garageId: newOverride.garageId,
      garageName: resolvedGarageName ?? newOverride.garageId,
      ipn: newOverride.ipn,
      shortTermLimit: newOverride.shortTermLimit,
      longTermLimit: newOverride.longTermLimit,
    };
    dispatch(
      updateSearchRateLimitConfigRequest({
        ...config,
        garageOverrides: {
          ...overrides,
          [override.garageId]: override,
        },
      }),
    );
    resetModal();
    onSuccess?.();
  }, [newOverride, resolvedGarageName, config, overrides, dispatch, resetModal, onSuccess]);

  const handleConfirmRemove = useCallback(
    (garageId: string) => {
      if (!config) {
        return;
      }
      const updatedOverrides = { ...overrides };
      delete updatedOverrides[garageId];
      dispatch(updateSearchRateLimitConfigRequest({ ...config, garageOverrides: updatedOverrides }));
      setConfirmRemoveId(null);
      onSuccess?.();
    },
    [config, overrides, dispatch, onSuccess],
  );

  const handleRequestRemove = useCallback((garageId: string) => setConfirmRemoveId(garageId), []);
  const handleCancelRemove = useCallback(() => setConfirmRemoveId(null), []);

  const handleGarageIdChange = useCallback((v: string) => setNewOverride((prev) => ({ ...prev, garageId: v })), []);
  const handleIpnChange = useCallback((v: string) => setNewOverride((prev) => ({ ...prev, ipn: v })), []);
  const handleShortTermLimitChange = useCallback(
    (v: string | undefined) =>
      setNewOverride((prev) => ({ ...prev, shortTermLimit: v ? Number.parseInt(v, 10) : undefined })),
    [],
  );
  const handleLongTermLimitChange = useCallback(
    (v: string | undefined) =>
      setNewOverride((prev) => ({ ...prev, longTermLimit: v ? Number.parseInt(v, 10) : undefined })),
    [],
  );

  const getRowKey = useCallback((row: GarageLimitOverride) => row.garageId ?? '', []);

  const columns: ColumnsType<GarageLimitOverride> = [
    {
      title: t('backoffice.search-rate-limit.overrides.col.garage', 'Garage'),
      dataIndex: 'garageId',
      render: (_: unknown, row: GarageLimitOverride) => (
        <Flex direction={'column'} gap={2}>
          <Text type={'h4_paragraph'}>{row.garageName}</Text>
          <Text type="base">{row.garageId}</Text>
        </Flex>
      ),
    },
    {
      title: t('backoffice.search-rate-limit.overrides.col.ipn', 'IPN'),
      dataIndex: 'ipn',
      render: (ipn: string) => <Text type="base">{ipn || '—'}</Text>,
    },
    {
      title: t('backoffice.search-rate-limit.overrides.col.short-term', 'Short-term'),
      dataIndex: 'shortTermLimit',
      render: (v: number | undefined) => <Text type="base">{v ?? t('common.default', '(default)')}</Text>,
    },
    {
      title: t('backoffice.search-rate-limit.overrides.col.long-term', 'Long-term'),
      dataIndex: 'longTermLimit',
      render: (v: number | undefined) => <Text type="base">{v ?? t('common.default', '(default)')}</Text>,
    },
    {
      title: '',
      key: 'actions',
      render: (_: unknown, row: GarageLimitOverride) => (
        <OverrideActionsCell
          garageId={row.garageId ?? ''}
          isConfirming={confirmRemoveId === row.garageId}
          onRequestRemove={handleRequestRemove}
          onConfirmRemove={handleConfirmRemove}
          onCancelRemove={handleCancelRemove}
        />
      ),
    },
  ];

  return (
    <>
      <MarginBox mb={12}>
        <WhiteButton onClick={openModal} data-cy={'add-garage-override'}>
          <Text type={'h4_paragraph'}>{t('backoffice.search-rate-limit.overrides.add', '+ Add garage override')}</Text>
        </WhiteButton>
      </MarginBox>

      {overridesList.length === 0 ? (
        <Flex justify={'center'}>
          <Text type="base">
            {t('backoffice.search-rate-limit.overrides.empty', 'No garage overrides configured yet')}
          </Text>
        </Flex>
      ) : (
        <Table<GarageLimitOverride>
          columns={columns}
          dataSource={overridesList}
          rowKey={getRowKey}
          pagination={false}
          size={'middle'}
        />
      )}

      <Modal
        title={
          <Text type="heading4">{t('backoffice.search-rate-limit.overrides.modal.title', 'Add Garage Override')}</Text>
        }
        open={showModal}
        onCancel={resetModal}
        width={480}
        footer={
          <Flex gap={10} justify={'flex-end'}>
            <YellowButton
              onClick={handleAddOverride}
              disabled={!newOverride.garageId || !newOverride.ipn}
              data-cy={'confirm-add-garage-override'}
            >
              <Text type={'h4_paragraph'}>{t('backoffice.search-rate-limit.overrides.modal.add', 'Add override')}</Text>
            </YellowButton>
            <WhiteButton onClick={resetModal}>
              <Text type={'h4_paragraph'}>{t('common.cancel', 'Cancel')}</Text>
            </WhiteButton>
          </Flex>
        }
      >
        <Flex direction={'column'} gap={15}>
          <Flex direction={'column'} gap={5}>
            <Text type="base">{t('backoffice.search-rate-limit.overrides.field.ipn', 'IPN *')}</Text>
            <Input
              bordered
              value={newOverride.ipn}
              onChange={handleIpnChange}
              placeholder={'e.g., IPN-00123'}
              data-cy={'override-ipn'}
            />
          </Flex>
          <Flex direction={'column'} gap={5}>
            <Text type="base">{t('backoffice.search-rate-limit.overrides.field.garageId', 'Garage ID *')}</Text>
            <Input
              bordered
              value={newOverride.garageId}
              onChange={handleGarageIdChange}
              placeholder={'e.g., garage-paris'}
              data-cy={'override-garage-id'}
            />
            {resolvedGarageName && <Text type="base">{resolvedGarageName}</Text>}
          </Flex>
          <Flex gap={15}>
            <Flex direction={'column'} gap={5}>
              <Text type="base">
                {t('backoffice.search-rate-limit.overrides.field.shortTermLimit', 'Short-Term Limit')}
              </Text>
              <InputNumber
                min={1}
                value={newOverride.shortTermLimit}
                onChange={handleShortTermLimitChange}
                placeholder={'Default'}
              />
            </Flex>
            <Flex direction={'column'} gap={5}>
              <Text type="base">
                {t('backoffice.search-rate-limit.overrides.field.longTermLimit', 'Long-Term Limit')}
              </Text>
              <InputNumber
                min={1}
                value={newOverride.longTermLimit}
                onChange={handleLongTermLimitChange}
                placeholder={'Default'}
              />
            </Flex>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
